import React, { useState, useEffect } from 'react';
import { X, Search, Check } from 'lucide-react';

const ResourceSelectionModal = ({ 
  isOpen, 
  onClose, 
  onSelect, 
  category, 
  title = "Select Resources" 
}) => {
  const [resources, setResources] = useState([]);
  const [filteredResources, setFilteredResources] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedResources, setSelectedResources] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchResources();
    }
  }, [isOpen, category]);

  useEffect(() => {
    filterResources();
  }, [resources, searchQuery]);

  const fetchResources = async () => {
    setLoading(true);
    try {
      const url = category 
        ? `/api/resources?category=${category}` 
        : '/api/resources';
      const response = await fetch(url);
      const data = await response.json();
      setResources(data);
    } catch (error) {
      console.error('Failed to fetch resources:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterResources = () => {
    let filtered = resources;
    
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(resource => 
        resource.name?.toLowerCase().includes(q)
      );
    }
    
    setFilteredResources(filtered);
  };

  const handleResourceToggle = (resource) => {
    setSelectedResources(prev => {
      const isSelected = prev.some(r => r._id === resource._id);
      if (isSelected) {
        return prev.filter(r => r._id !== resource._id);
      } else {
        return [...prev, resource];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedResources.length === filteredResources.length) {
      setSelectedResources([]);
    } else {
      setSelectedResources(filteredResources);
    }
  };

  const handleConfirm = () => {
    onSelect(selectedResources);
    setSelectedResources([]);
    setSearchQuery('');
    onClose();
  };

  const handleCancel = () => {
    setSelectedResources([]);
    setSearchQuery('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-4/5 max-w-4xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          {/* Header */}
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              {title} {category && `(${category})`}
            </h3>
            <button
              onClick={handleCancel}
              className="text-gray-400 hover:text-gray-600"
            >
              <X size={24} />
            </button>
          </div>

          {/* Search Bar */}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search resources..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Select All Button */}
          {filteredResources.length > 0 && (
            <div className="mb-4">
              <button
                onClick={handleSelectAll}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                {selectedResources.length === filteredResources.length ? 'Deselect All' : 'Select All'}
              </button>
            </div>
          )}

          {/* Resources List */}
          <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
            {loading ? (
              <div className="p-6 text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <p className="mt-2 text-gray-600">Loading resources...</p>
              </div>
            ) : filteredResources.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                No resources found matching your search.
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {filteredResources.map((resource) => {
                  const isSelected = selectedResources.some(r => r._id === resource._id);
                  return (
                    <div
                      key={resource._id}
                      onClick={() => handleResourceToggle(resource)}
                      className={`p-4 cursor-pointer hover:bg-gray-50 flex items-center justify-between ${
                        isSelected ? 'bg-blue-50' : ''
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <div className={`w-5 h-5 border-2 rounded flex items-center justify-center ${
                          isSelected ? 'border-blue-600 bg-blue-600' : 'border-gray-300'
                        }`}>
                          {isSelected && <Check size={12} className="text-white" />}
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">{resource.name}</div>
                          {resource.description && (
                            <div className="text-sm text-gray-500">{resource.description}</div>
                          )}
                        </div>
                      </div>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        resource.category === 'Mechanical' 
                          ? 'bg-blue-100 text-blue-800' 
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {resource.category === 'Mechanical' ? '🔧' : '⚡'} {resource.category}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Selected Count */}
          {selectedResources.length > 0 && (
            <div className="mt-4 text-sm text-gray-600">
              {selectedResources.length} resource{selectedResources.length !== 1 ? 's' : ''} selected
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-4 mt-6 border-t">
            <button
              onClick={handleCancel}
              className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={selectedResources.length === 0}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Add {selectedResources.length} Resource{selectedResources.length !== 1 ? 's' : ''}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResourceSelectionModal;
